import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  FileText,
  Hash,
  MessageCircle,
  Search,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { ThemeToggle } from "@siena-ai/design-system";
import { FolderIconSafe as FolderIcon } from "./FolderIconSafe";
import { ReaderMenu } from "./ReaderMenu";
import type {
  ManifestFolder,
  ManifestPageEntry,
  SiteManifest,
} from "../types";
import { openLiveChat } from "../lib/liveChat";
import { ensureSearchIndex, runSearch, type SearchHit } from "../search";

interface HomepageLandingViewProps {
  manifest: SiteManifest;
  /**
   * Accept an optional anchor so section hits returned by the search
   * index can deep-link straight to the matched heading instead of just
   * the article. The Router-level `navigateTo` already supports it.
   */
  onNavigate: (slug: string | null, anchor?: string | null) => void;
  /**
   * The folder currently open, or `null` for the top-level index. This is
   * driven by the URL (`/f/<path>`) and owned by the Router, so the logo,
   * back/forward, and deep links all stay in sync with what's rendered.
   */
  activeFolderId: string | null;
  /** Navigate to a folder (uuid) or back to the index (`null`). */
  onSelectFolder: (folderId: string | null) => void;
}

/** Escape regex special characters before splicing user input into a pattern. */
function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Wrap query-term matches in `<mark>` so both result titles and body
 * snippets visually surface the part the reader searched for. Returns
 * the original string untouched when there are no terms or no matches.
 */
function highlight(text: string, terms: string[]): React.ReactNode {
  if (!text) return text;
  const escaped = terms.map((t) => escapeRegex(t.trim())).filter(Boolean);
  if (escaped.length === 0) return text;

  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <mark
        key={parts.length}
        className="bg-yellow-200/80 dark:bg-yellow-500/30 text-foreground rounded-sm px-0.5"
      >
        {match[0]}
      </mark>,
    );
    last = match.index + match[0].length;
    if (match[0].length === 0) re.lastIndex++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/**
 * Walk `parent_folder_uuid` from the active folder up to the root and
 * return the chain in display order (top ancestor → active folder).
 * Storing only the deepest uuid in the URL hash is enough — we can
 * always recover the breadcrumb at render time, so back/forward keeps
 * working without packing an array into the URL.
 */
function getFolderPath(
  folderId: string,
  foldersById: Map<string, ManifestFolder>,
): ManifestFolder[] {
  const path: ManifestFolder[] = [];
  const seen = new Set<string>();
  let current = foldersById.get(folderId);
  while (current && !seen.has(current.uuid)) {
    seen.add(current.uuid);
    path.unshift(current);
    current = current.parent_folder_uuid
      ? foldersById.get(current.parent_folder_uuid)
      : undefined;
  }
  return path;
}

export const HomepageLandingView: React.FC<HomepageLandingViewProps> = ({
  manifest,
  onNavigate,
  activeFolderId,
  onSelectFolder,
}) => {
  const { space } = manifest;
  const branding = space.branding;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchHits, setSearchHits] = useState<SearchHit[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchReqRef = useRef(0);

  // The active folder is owned by the Router (URL-derived) and passed in.
  const activeFolder = activeFolderId;
  const navigateToFolder = onSelectFolder;

  const foldersById = useMemo(() => {
    const map = new Map<string, ManifestFolder>();
    for (const folder of manifest.folders) {
      map.set(folder.uuid, folder);
    }
    return map;
  }, [manifest.folders]);

  /**
   * Folders form a tree (`parent_folder_uuid`). The homepage renders
   * only top-level cards, but pages can live arbitrarily deep
   * (e.g. year → week → release). We need the page count on each card
   * to reflect EVERY descendant so a "2026" card with pages nested
   * under sub-folders still shows up instead of being filtered out for
   * having 0 direct pages.
   */
  const { pagesByFolder, childrenByFolder } = useMemo(() => {
    const pagesMap = new Map<string, ManifestPageEntry[]>();
    for (const page of manifest.pages) {
      const arr = pagesMap.get(page.folder_uuid) ?? [];
      arr.push(page);
      pagesMap.set(page.folder_uuid, arr);
    }
    const childrenMap = new Map<string | null, ManifestFolder[]>();
    for (const folder of manifest.folders) {
      const key = folder.parent_folder_uuid ?? null;
      const arr = childrenMap.get(key) ?? [];
      arr.push(folder);
      childrenMap.set(key, arr);
    }
    for (const arr of childrenMap.values()) {
      arr.sort((a, b) => a.position - b.position);
    }
    return { pagesByFolder: pagesMap, childrenByFolder: childrenMap };
  }, [manifest]);

  const countDescendantPages = useCallback(
    (folderUuid: string): number => {
      let total = pagesByFolder.get(folderUuid)?.length ?? 0;
      const queue = [...(childrenByFolder.get(folderUuid) ?? [])];
      while (queue.length > 0) {
        const child = queue.shift()!;
        total += pagesByFolder.get(child.uuid)?.length ?? 0;
        const grandchildren = childrenByFolder.get(child.uuid);
        if (grandchildren) queue.push(...grandchildren);
      }
      return total;
    },
    [pagesByFolder, childrenByFolder],
  );

  const topLevelFolders = useMemo(
    () =>
      (childrenByFolder.get(null) ?? [])
        .map((f) => ({ ...f, pageCount: countDescendantPages(f.uuid) }))
        .filter((f) => f.pageCount > 0),
    [childrenByFolder, countDescendantPages],
  );

  /**
   * Reuse the MiniSearch index DocsSiteLayout already builds so the
   * landing search matches title, headings, and body (not just the
   * title + description substring filter we had before), respects the
   * field boosts, and shares scoring rules with the in-article search.
   * Falls back to a simple title/description match if the index can't
   * be loaded for any reason (offline, 404, etc.).
   */
  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchHits(null);
      setSearchLoading(false);
      return;
    }

    const reqId = ++searchReqRef.current;
    setSearchLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const mini = await ensureSearchIndex(manifest);
        if (reqId !== searchReqRef.current) return;
        setSearchHits(runSearch(mini, query, 20));
        setSearchLoading(false);
      } catch {
        if (reqId !== searchReqRef.current) return;
        const q = query.toLowerCase();
        const fallback: SearchHit[] = manifest.pages
          .filter(
            (p) =>
              p.title.toLowerCase().includes(q) ||
              (p.description && p.description.toLowerCase().includes(q)),
          )
          .slice(0, 20)
          .map((p) => ({
            id: p.uuid,
            pageUuid: p.uuid,
            pageSlug: p.slug,
            pageTitle: p.title,
            pageEmoji: p.emoji ?? null,
            folderName: foldersById.get(p.folder_uuid)?.name ?? null,
            sectionTitle: p.title,
            anchor: null,
            snippet: p.description ?? "",
            score: 1,
            isSection: false,
          }));
        setSearchHits(fallback);
        setSearchLoading(false);
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [searchQuery, manifest, foldersById]);

  const queryTerms = useMemo(
    () => searchQuery.trim().split(/\s+/).filter(Boolean),
    [searchQuery],
  );

  const breadcrumb = useMemo(
    () => (activeFolder ? getFolderPath(activeFolder, foldersById) : []),
    [activeFolder, foldersById],
  );

  const showingSearch = searchQuery.trim().length > 0;

  const seoTitle = space.seo.metaTitle || space.name;
  const seoDescription = space.seo.metaDescription;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDescription && <meta name="description" content={seoDescription} />}
        {branding.faviconUrl && (
          <>
            <link rel="icon" href={branding.faviconUrl} sizes="any" type={branding.faviconUrl.endsWith(".svg") ? "image/svg+xml" : "image/png"} />
            <link rel="icon" href={branding.faviconUrl} sizes="32x32" />
            <link rel="apple-touch-icon" sizes="180x180" href={branding.faviconUrl} />
          </>
        )}
        {!space.seo.allowIndexing && (
          <meta name="robots" content="noindex, nofollow" />
        )}
      </Helmet>
      <div
        className="min-h-screen bg-background"
        style={
          space.brandColor
            ? ({ "--brand-color": space.brandColor } as React.CSSProperties)
            : undefined
        }
      >
        <header>
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <a href="#" onClick={(e) => { e.preventDefault(); onNavigate(null); }} className="cursor-pointer flex items-center">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={space.name}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain dark:hidden"
                />
              ) : (
                <span className="text-sm font-semibold">{space.name}</span>
              )}
              {branding.logoDarkUrl && (
                <img
                  src={branding.logoDarkUrl}
                  alt={space.name}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain hidden dark:block"
                />
              )}
            </a>
            <div className="flex items-center gap-2">
              <ThemeToggle variant="ghost" size="icon-sm" />
              <ReaderMenu />
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-6 pb-16">
          <section className="pt-12 pb-10 text-center">
            <h1 className="text-2xl mb-2">
              {branding.homepageHeading || space.name}
            </h1>
            {branding.homepageDescription && (
              <p className="text-sm text-muted-foreground mb-6">
                {branding.homepageDescription}
              </p>
            )}

            {space.linkedLiveChatId &&
              space.liveChatScriptUrl &&
              space.liveChatAppKey && (
                <div className="max-w-lg mx-auto mb-5">
                  <button
                    onClick={() => {
                      openLiveChat().catch((err) => {
                        // eslint-disable-next-line no-console
                        console.warn(
                          "[HomepageLandingView] openLiveChat failed",
                          err,
                        );
                      });
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border bg-card text-sm text-muted-foreground hover:bg-muted/50 transition-colors text-left"
                  >
                    <MessageCircle className="h-4 w-4 shrink-0" />
                    Ask me anything...
                  </button>
                </div>
              )}
          </section>

          <section className="pb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value) navigateToFolder(null);
                }}
                onFocus={() => {
                  void ensureSearchIndex(manifest).catch(() => {
                    /* fallback handles this in the search effect */
                  });
                }}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring/30"
              />
            </div>
          </section>

          {showingSearch ? (
            <section className="pb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium text-muted-foreground !font-sans">
                  {searchLoading && searchHits === null
                    ? "Searching…"
                    : `${searchHits?.length ?? 0} result${(searchHits?.length ?? 0) !== 1 ? "s" : ""
                    } for "${searchQuery.trim()}"`}
                </h2>
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear search
                </button>
              </div>

              {searchLoading && searchHits === null ? (
                <div className="rounded-lg border bg-card py-12 text-center">
                  <p className="text-sm text-muted-foreground">Searching…</p>
                </div>
              ) : searchHits && searchHits.length === 0 ? (
                <div className="rounded-lg border bg-card py-12 text-center">
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center mx-auto mb-3">
                    <Search className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium mb-1">No pages found</p>
                  <p className="text-xs text-muted-foreground">
                    Try a different term.
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border bg-card divide-y">
                  {(searchHits ?? []).map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => onNavigate(hit.pageSlug, hit.anchor)}
                      className="w-full text-left px-4 py-3 text-sm hover:bg-muted/50 transition-colors flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-foreground">
                          {hit.pageEmoji ? (
                            <FolderIcon
                              emoji={hit.pageEmoji}
                              iconClassName="h-3.5 w-3.5"
                            />
                          ) : (
                            <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <span className="font-medium truncate">
                            {highlight(hit.pageTitle, queryTerms)}
                          </span>
                        </div>
                        {hit.isSection &&
                          hit.sectionTitle &&
                          hit.sectionTitle !== hit.pageTitle && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 ml-5">
                              <Hash className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                {highlight(hit.sectionTitle, queryTerms)}
                              </span>
                            </div>
                          )}
                        {hit.snippet && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 ml-5">
                            {highlight(hit.snippet, queryTerms)}
                          </p>
                        )}
                        {hit.folderName && (
                          <p className="text-[11px] text-muted-foreground/80 mt-1 ml-5">
                            {hit.folderName}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : activeFolder ? (
            <section>
              {(() => {
                const col = foldersById.get(activeFolder);
                if (!col) return null;

                /**
                 * A folder can hold pages, sub-folders, or both. We render
                 * direct pages first, then each non-empty sub-folder as its
                 * own card so readers can drill deeper into a tree like
                 * year → week → release without hitting an empty screen.
                 */
                const directPages = (pagesByFolder.get(activeFolder) ?? [])
                  .slice()
                  .sort((a, b) => a.position - b.position);
                const subFolders = (childrenByFolder.get(activeFolder) ?? [])
                  .map((f) => ({
                    ...f,
                    pageCount: countDescendantPages(f.uuid),
                  }))
                  .filter((f) => f.pageCount > 0);
                const totalPages = countDescendantPages(activeFolder);

                return (
                  <>
                    <nav
                      aria-label="Folder breadcrumb"
                      className="flex items-center gap-1 text-sm text-muted-foreground mb-4 flex-wrap"
                    >
                      <button
                        onClick={() => navigateToFolder(null)}
                        className="hover:text-foreground transition-colors inline-flex items-center gap-1"
                      >
                        <ChevronRight className="h-3.5 w-3.5 rotate-180 shrink-0" />
                        All folders
                      </button>
                      {breadcrumb.map((folder, idx) => {
                        const isLast = idx === breadcrumb.length - 1;
                        return (
                          <React.Fragment key={folder.uuid}>
                            <ChevronRight className="h-3 w-3 shrink-0" />
                            {isLast ? (
                              <span className="text-foreground font-medium inline-flex items-center gap-1 truncate max-w-[180px]">
                                {folder.emoji && (
                                  <FolderIcon
                                    emoji={folder.emoji}
                                    iconClassName="h-3.5 w-3.5"
                                  />
                                )}
                                <span className="truncate">{folder.name}</span>
                              </span>
                            ) : (
                              <button
                                onClick={() => navigateToFolder(folder.uuid)}
                                className="hover:text-foreground transition-colors inline-flex items-center gap-1 truncate max-w-[140px]"
                              >
                                {folder.emoji && (
                                  <FolderIcon
                                    emoji={folder.emoji}
                                    iconClassName="h-3.5 w-3.5"
                                  />
                                )}
                                <span className="truncate">{folder.name}</span>
                              </button>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </nav>

                    <div className="flex items-center gap-2.5 mb-5">
                      {col.emoji && (
                        <FolderIcon
                          emoji={col.emoji}
                          className="text-xl"
                          iconClassName="h-5 w-5"
                        />
                      )}
                      <h2 className="text-base font-semibold !font-sans">
                        {col.name}
                      </h2>
                      <span className="text-xs text-muted-foreground">
                        {totalPages} page{totalPages !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {directPages.length > 0 && (
                      <div className="rounded-lg border bg-card divide-y mb-4">
                        {directPages.map((page) => (
                          <button
                            key={page.uuid}
                            onClick={() => onNavigate(page.slug)}
                            className="w-full text-left px-4 py-3 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between gap-4"
                          >
                            <span className="text-foreground">{page.title}</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}

                    {subFolders.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {subFolders.map((folder) => (
                          <button
                            key={folder.uuid}
                            onClick={() => navigateToFolder(folder.uuid)}
                            className="text-left rounded-lg border bg-card hover:bg-muted/30 transition-colors p-4 flex items-start gap-3"
                          >
                            {folder.emoji && (
                              <FolderIcon
                                emoji={folder.emoji}
                                className="text-xl mt-0.5"
                                iconClassName="h-5 w-5 mt-0.5"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">
                                {folder.name}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1.5">
                                {folder.pageCount} page
                                {folder.pageCount !== 1 ? "s" : ""}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {directPages.length === 0 && subFolders.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No pages in this folder yet.
                      </p>
                    )}
                  </>
                );
              })()}
            </section>
          ) : (
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-4 !font-sans">
                Browse by topic
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {topLevelFolders.map((folder) => (
                  <button
                    key={folder.uuid}
                    onClick={() => navigateToFolder(folder.uuid)}
                    className="text-left rounded-lg border bg-card hover:bg-muted/30 transition-colors p-4 flex items-start gap-3"
                  >
                    {folder.emoji && (
                      <FolderIcon
                        emoji={folder.emoji}
                        className="text-xl mt-0.5"
                        iconClassName="h-5 w-5 mt-0.5"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{folder.name}</div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {folder.pageCount} page
                        {folder.pageCount !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
};
